import { defineStore } from 'pinia'
import { useStorage, StorageSerializers } from '@vueuse/core'
import { ref, computed } from 'vue'
import type { Session } from '@supabase/supabase-js'
import apicore from '@/plugins/api'
import { supabase } from '@/plugins/supabase'
import { preferredLocale } from '@/plugins/i18n'
import { AI_UI_ENABLED as AI_UI_BUILD_FLAG } from '@/config/ai/features'
import { USER_INFO_STORAGE_KEY } from '@/config/deck/constants'

export interface UserInfoLdap {
  uid: string
  mail: string
  cn: string
  givenName: string
  sn: string
  title: string
  afpRegroupCateg: string
  preferredLanguage: string
  ctr: string
  description: string
}

export interface UserInfo {
  username: string
  infosLdap?: UserInfoLdap
}

// Enregistre la langue d'usage de l'utilisateur (statistiques).
// Jamais bloquant : un échec ne doit pas perturber la session.
async function syncProfileLocale(userId: string) {
  try {
    await supabase
      .from('profiles')
      .update({ locale: preferredLocale.value })
      .eq('id', userId)
  } catch {
    /* non bloquant */
  }
}

export const useUserStore = defineStore('user', () => {
  const userInfo = useStorage<UserInfo | null>(
    USER_INFO_STORAGE_KEY,
    null,
    localStorage,
    { serializer: StorageSerializers.object }
  )

  const displayName = computed(() => {
    if (!userInfo.value) return null
    const ldap = userInfo.value.infosLdap
    if (!ldap) return userInfo.value.username
    return ldap.givenName && ldap.sn ? `${ldap.givenName} ${ldap.sn}` : ldap.cn
  })

  const jobTitle = computed(() => userInfo.value?.infosLdap?.title ?? null)
  const service = computed(() => userInfo.value?.infosLdap?.description ?? null)
  const preferredLanguage = computed(
    () => userInfo.value?.infosLdap?.preferredLanguage ?? null
  )

  async function fetchUserInfo() {
    const { user } = await apicore.getUserInfo()
    userInfo.value = {
      username: user.username,
      infosLdap: user.additionalProperties?.infosLdap
    }
  }

  const aiEnabledClaim = ref(false)

  const aiEnabled = computed(() => AI_UI_BUILD_FLAG && aiEnabledClaim.value)

  // Décode les claims directement depuis le payload du JWT — évite de
  // reprendre le lock auth Supabase, qui peut deadlock s'il est appelé depuis
  // onAuthStateChange.
  function loadFromSession(session: Session | null) {
    if (!session?.access_token) {
      aiEnabledClaim.value = false
      return
    }
    try {
      const payload = JSON.parse(atob(session.access_token.split('.')[1]))
      aiEnabledClaim.value = payload.ai_enabled === true
    } catch {
      aiEnabledClaim.value = false
    }
    void syncProfileLocale(session.user.id)
  }

  function clear() {
    userInfo.value = null
    aiEnabledClaim.value = false
  }

  return {
    userInfo,
    displayName,
    jobTitle,
    service,
    preferredLanguage,
    fetchUserInfo,
    aiEnabled,
    loadFromSession,
    clear
  }
})
